import { AlertCircle, Info } from "lucide-react";

/**
 * PlatformFeeNotice — reusable in-app disclosure about the future platform
 * service fee for real, paid projects (and a softer variant for the demo
 * marketplace shown to regular users).
 *
 * Variants:
 *   - "demo"    → soft beta notice for regular users on the demo marketplace.
 *                 Does NOT mention fees applying to demos.
 *   - "real"    → stronger notice for Early Access members on real opportunities.
 *                 Mentions the future platform service fee explicitly.
 */
const DEMO_TEXT =
  "Beta notice: Last Bookmark is currently in testing. Demo projects do not involve real applications or payments. When paid projects officially launch, completed transactions will include a platform service fee to support secure payments and operation of the site.";

const REAL_TEXT =
  "Last Bookmark is currently in beta. During this testing phase, users may arrange payment independently at their own risk. Once secure in-platform payments launch, completed paid projects will include a platform service fee. The exact fee will be clearly shown before either party agrees to payment.";

export function PlatformFeeNotice({ variant = "demo", compact = false, className = "" }) {
  const isReal = variant === "real";
  const Icon = isReal ? AlertCircle : Info;
  const title = isReal ? "Payment and fee notice" : "Beta notice";
  const body = isReal ? REAL_TEXT : DEMO_TEXT;
  const tone = "lantern-notice";
  const iconTone = "text-[hsl(var(--sb-gold-2))] dark:text-[hsl(var(--sb-gold))]";

  return (
    <div
      className={`rounded-2xl ${tone} px-4 py-3 ${className}`}
      data-testid={`platform-fee-notice-${variant}`}
      role="note"
    >
      <div className="flex items-start gap-2.5">
        <Icon size={compact ? 14 : 16} className={`mt-0.5 shrink-0 ${iconTone}`} />
        <div className="min-w-0 flex-1">
          {!compact && (
            <p className={`text-xs font-bold uppercase tracking-[0.16em] ${iconTone}`}>
              {title}
            </p>
          )}
          <p
            className={`${compact ? "" : "mt-1.5"} text-sm leading-relaxed`}
          >
            {compact && <span className={`font-bold ${iconTone}`}>{title}. </span>}
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default PlatformFeeNotice;
